import { z } from 'zod';
import {
  createModel,
  currencySchema,
  imageWithUploadSchema,
  partySchema,
  QueryMany,
  QueryOne,
  refSchema,
  uTagsSchema,
} from '../../common';
import {
  dateSchema,
  idSchema,
  nameSchema,
  objectIdSchema,
  priceSchema,
} from '../../validation';

/**
 * `BillingTransaction` — one row per settled gateway event.
 *
 * Pure payment ledger. Does NOT touch the freight `Wallet` (which is
 * what keeps the freight invariant safe).
 *
 * ## Created on webhook, not on attempt
 *
 * A BTx row is written when Moyasar tells us something happened — a
 * `payment.paid`, `payment.failed`, or `payment.refunded` webhook (or
 * the cron's polled equivalent). No row is created speculatively at
 * "Pay click" or invoice mint time. Consequence: BTx never has a
 * `pending` status — every row reflects a terminal gateway event.
 *
 * Idempotency is enforced by a unique index on `gateway.paymentId` —
 * a re-delivered webhook is a no-op.
 *
 * ## Pointers, not snapshots
 *
 * BTx holds pointer-only refs (`billingId`, `subscriptionId`) — the
 * BTx row is an audit / debug record and the related Billing /
 * Subscription docs are always loaded on demand. No display anchors
 * embedded.
 *
 * ## Gateway
 *
 * `gateway` is the unified payment-surface envelope. Holds the
 * provider, the inferred method (`card` / `mada` / `apple_pay` /
 * `stcpay`), the external invoice + payment ids, and the most-recent
 * response code / message. `paymentId` is required (this is the
 * Moyasar `/payments/:id` we matched the webhook against and is the
 * BTx-level idempotency key).
 */
export const BillingTransactionSchema = createModel({
  /**
   * Human-readable ref (BTX-2026-03-000123). Also used as Moyasar
   * `given_id` for idempotency on outbound calls.
   */
  ref: refSchema,

  /** Owner — Party snapshot of the paying org. */
  org: partySchema,
  /** Internal Mongo ref to the parent Billing. */
  billingId: objectIdSchema,
  /** Internal Mongo ref to the Subscription when Billing is subscription-sourced. */
  subscriptionId: objectIdSchema.optional(),

  /** Operation type. */
  type: z.enum(['charge', 'refund', 'chargeback']),
  /**
   * Lifecycle.
   *
   * - `success` — money settled (paid / refunded).
   * - `failed` — gateway returned a terminal failure (declined / voided).
   * - `waiting_for_approval` — enterprise bank-transfer path (not
   *    active in v1; reserved for future manual-approval flow).
   */
  status: z.enum(['success', 'failed', 'waiting_for_approval']),

  /** Always positive; direction encoded by `type`. */
  amount: priceSchema,
  /** Inherits from parent Billing. */
  currency: currencySchema,

  /**
   * Unified gateway envelope. Holds everything the gateway gives us:
   * provider, method, external invoice + payment ids, and the
   * most-recent response code / message.
   */
  gateway: z.object({
    provider: z.enum(['moyasar']),
    /**
     * Payment method inferred from the gateway response.
     */
    method: z.enum(['card', 'mada', 'apple_pay', 'stcpay']).optional(),
    /** Moyasar `/invoices/:id` — the parent invoice (lives on `Billing.checkout.id` too). */
    invoiceId: idSchema.optional(),
    /**
     * Moyasar `/payments/:id` — required; the BTx-level idempotency
     * key (unique-indexed at the DB layer). Every BTx is born from a
     * Moyasar payment, so this is always known.
     */
    paymentId: idSchema,
    /**
     * Gateway processing fee in MAJOR units (same currency as the
     * sibling `amount`).
     *
     * - `charge` (paid): the fee Moyasar deducted from this
     *   settlement (e.g. `7.49` SAR on a `499.50` SAR charge).
     * - `charge` (failed): `0` — Moyasar doesn't fee declines.
     * - `refund`: `0` — Moyasar doesn't refund the original fee.
     *
     * Optional because pre-existing rows + future non-Moyasar
     * providers may not carry one. Used for internal margin /
     * payout reconciliation only — never customer-facing math.
     */
    fee: priceSchema.optional(),
    response: z
      .object({
        code: z.string().optional(),
        message: z.string().optional(),
      })
      .optional(),
  }),

  /** Universal tags — free-form, namespace:value convention. */
  uTags: uTagsSchema,

  /** Proof of payment images (enterprise bank transfer). */
  images: z.array(imageWithUploadSchema).default([]),
  /** Free-form notes (e.g. admin annotation on a manual refund). */
  notes: nameSchema.optional(),

  /** Approval lifecycle (mirrors freight pattern for `waiting_for_approval`). */
  approvedAt: dateSchema.optional(),
  approvedBy: nameSchema.optional(),
  rejectedAt: dateSchema.optional(),
  rejectedBy: nameSchema.optional(),
  rejectionReason: nameSchema.optional(),
});

export type BillingTransaction = z.infer<typeof BillingTransactionSchema.model>;
export type BillingTransactionData = z.infer<typeof BillingTransactionSchema.data>;
export type BillingTransactionPartial = z.infer<
  typeof BillingTransactionSchema.partial
>;
export type BillingTransactionQueryMany = QueryMany<BillingTransaction>;
export type BillingTransactionQueryOne = QueryOne<BillingTransaction>;
